import React, { useState, useEffect } from 'react';
import { 
  Eye,
  MessageCircle,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Search
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const FollowupModal = ({ followups, onClose }) => {
  const sortedFollowups = [...followups].sort((a, b) => 
    new Date(b.followup_date) - new Date(a.followup_date)
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-96 max-h-[80vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800">Followup History</h2>
          <button 
            onClick={onClose} 
            className="text-gray-600 hover:text-gray-800"
          >
            ✕
          </button>
        </div>
        
        {sortedFollowups.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No followups available
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {sortedFollowups.map((followup) => (
              <li key={followup.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    {followup.status === 'incomplete' ? (
                      <AlertCircle className="text-yellow-500 mr-2 w-4 h-4" />
                    ) : (
                      <CheckCircle className="text-green-500 mr-2 w-4 h-4" />
                    )}
                    <span className="font-medium text-sm text-gray-700">
                      Followup on {formatDate(followup.followup_date)}
                    </span>
                  </div>
                  <span 
                    className={`text-xs px-2 py-1 rounded-full ${
                      followup.status === 'incomplete' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {followup.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 italic pl-6">
                  "{followup.notes}"
                </p>
                <div className="text-xs text-gray-500 text-right mt-1">
                  Created on {formatDate(followup.created_at)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const LeadStatusChart = ({ data }) => {
  const getBarColor = (status) => {
    const colorMap = {
      'Interested': '#4CAF50',
      'Call Answered': '#2196F3',
      'Call Not Answered': '#F44336',
      'Not Interested': '#FF9800',
      'Online Demo Taken': '#3F51B5',
      'Gurgaon Campus Visited': '#FFC107',
      'Online Admission Taken': '#009688',
      'Gurgaon Admission Taken': '#9C27B0',
      'Followup': '#E91E63'
    };
    
    return colorMap[status] || '#8D43F8';
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Lead Status Distribution</h3>
      <div style={{ width: '100%', height: 250 }}>
        <ResponsiveContainer>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis 
              dataKey="lead_status" 
              angle={-45} 
              textAnchor="end" 
              height={70} 
              tick={{ fontSize: 12 }}
            />
            <YAxis allowDecimals={false} />
            <Tooltip
              formatter={(value) => [`${value} leads`, 'Count']}
              contentStyle={{ 
                borderRadius: '8px', 
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', 
                border: 'none' 
              }}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {data.map((entry) => (
                <Cell key={`cell-${entry.lead_status}`} fill={getBarColor(entry.lead_status)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const StudentLead = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [studentDetails, setStudentDetails] = useState({});
  const [followups, setFollowups] = useState({});
  const [selectedLeadFollowups, setSelectedLeadFollowups] = useState(null);
  const [leadStats, setLeadStats] = useState({
    totalLeads: 0,
    interestedLeads: 0,
    otherLeads: 0
  });
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ 
    key: 'created_at', 
    direction: 'desc' 
  });
  const [leadStatusData, setLeadStatusData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(true);


  const fetchLeadStatusDistribution = async (signal) => {
    setLoadingChart(true);
    
    try {
      const response = await fetch('https://apissc.sainikschoolcadet.com/api/student-activity/lead-status/counts', { signal });
      const result = await response.json();
      
      if (result.success && Array.isArray(result.data)) {
        setLeadStatusData(result.data);
      } else {
        console.error('Invalid data format for lead status counts');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Failed to fetch lead status distribution:', err);
    } finally {
      if (!signal || !signal.aborted) {
        setLoadingChart(false);
      }
    }
  };

  const calculateLeadStats = (leadsData) => {
    const stats = {
      totalLeads: leadsData.length,
      interestedLeads: leadsData.filter(lead => lead.lead_status === 'Interested').length,
      otherLeads: leadsData.filter(lead => lead.lead_status !== 'Interested').length
    };
    setLeadStats(stats);
  };

  const fetchEnrichedData = async (leadsData, signal) => {
    try {
      const uniqueStudentIds = [...new Set(leadsData.map(lead => lead.s_id))];
      
      const studentDetailsPromises = uniqueStudentIds.map(async (studentId) => {
        try {
          const studentResponse = await fetch(`https://apissc.sainikschoolcadet.com/api/students/getbyid/${studentId}`, { signal });
          if (!studentResponse.ok) {
            return { s_id: studentId, error: true };
          }
          const data = await studentResponse.json();
          return data;
        } catch (err) {
          if (err.name === 'AbortError') throw err;
          return { s_id: studentId, error: true };
        }
      });

      const followupsPromises = leadsData.map(async (lead) => {
        try {
          const followupsResponse = await fetch(`https://apissc.sainikschoolcadet.com/api/activity-followup/activity/${lead.id}`, { signal });
          if (!followupsResponse.ok) {
            return { [lead.id]: [] };
          }
          const followupsData = await followupsResponse.json();
          return { [lead.id]: followupsData };
        } catch (err) {
          if (err.name === 'AbortError') throw err;
          return { [lead.id]: [] };
        }
      });

      const studentDetailsArray = await Promise.all(studentDetailsPromises);
      const followupsArray = await Promise.all(followupsPromises);

      if (signal && signal.aborted) return;

      const studentDetailsMap = studentDetailsArray.reduce((acc, student) => {
        if (!student.error) {
          acc[student.s_id] = student;
        }
        return acc;
      }, {});

      const followupsMap = followupsArray.reduce((acc, followup) => {
        return { ...acc, ...followup };
      }, {});

      setStudentDetails(studentDetailsMap);
      setFollowups(followupsMap);
      
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Failed to fetch enriched data:', err);
    }
  };

  const fetchStudentLeads = async (signal) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('https://apissc.sainikschoolcadet.com/api/student-activity/', { signal });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (signal && signal.aborted) return;

      if (Array.isArray(data)) {
        setLeads(data);
        calculateLeadStats(data);
        await fetchEnrichedData(data, signal);
      } else {
        setError('Invalid data format received from API');
      }
      
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Failed to fetch student leads:', err);
      setError(`Failed to fetch student leads: ${err.message}`);
    } finally {
      if (!signal || !signal.aborted) {
        setLoading(false);
      }
    }
  };

  const refreshData = () => {
    fetchStudentLeads();
    fetchLeadStatusDistribution();
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchStudentLeads(controller.signal);
    fetchLeadStatusDistribution(controller.signal);

    return () => {
      controller.abort();
    };
  }, []);

  const sortedLeads = [...leads].sort((a, b) => {
    if (sortConfig.key === 'created_at') {
      return sortConfig.direction === 'asc'
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at);
    }
    return 0;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' 
        ? 'asc' 
        : 'desc'
    }));
  };

  const handleViewStudent = (studentId) => {
    window.open(`/view-student/${studentId}`, '_blank', 'noopener,noreferrer');
  };

  const handleWhatsAppContact = (phoneNumber) => {
    const formattedNumber = phoneNumber?.startsWith('+91') 
      ? phoneNumber 
      : `+91${phoneNumber?.replace(/[^\d]/g, '') || ''}`;
    
    window.open(`https://wa.me/${formattedNumber}`, '_blank', 'noopener,noreferrer');
  };

  const handleViewFollowups = (leadId) => {
    setSelectedLeadFollowups(followups[leadId] || []);
  };

  const getLeadStatusColor = (status) => {
    switch(status) {
      case 'Interested':
        return 'bg-green-100 text-green-800';
      case 'Call Answered':
        return 'bg-blue-100 text-blue-800';
      case 'Call Not Answered':
        return 'bg-red-100 text-red-800';
      case 'Not Interested':
        return 'bg-orange-100 text-orange-800';
      case 'Online Demo Taken':
        return 'bg-indigo-100 text-indigo-800';
      case 'Gurgaon Campus Visited':
        return 'bg-yellow-100 text-yellow-800';
      case 'Online Admission Taken':
        return 'bg-teal-100 text-teal-800';
      case 'Gurgaon Admission Taken':
        return 'bg-purple-100 text-purple-800';
      case 'Followup':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredLeads = sortedLeads.filter(lead => {
    const student = studentDetails[lead.s_id] || {};
    const matchesSearch = !searchQuery || student.f_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || lead.lead_status?.toLowerCase() === filter.toLowerCase();
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Student Leads</h2>
        <div className="flex items-center space-x-4">
          <button 
            onClick={refreshData}
            className="text-gray-600 hover:text-blue-600 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Total Activity</p>
          <p className="text-2xl font-bold text-blue-600">{leadStats.totalLeads}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Interested</p>
          <p className="text-2xl font-bold text-green-600">
            {leadStats.interestedLeads}
            <span className="text-sm text-gray-500">
              {` (${Math.round((leadStats.interestedLeads / leadStats.totalLeads) * 100) || 0}%)`}
            </span>
          </p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Other Activity</p>
          <p className="text-2xl font-bold text-purple-600">
            {leadStats.otherLeads}
            <span className="text-sm text-gray-500">
              {` (${Math.round((leadStats.otherLeads / leadStats.totalLeads) * 100) || 0}%)`}
            </span>
          </p>
        </div>
      </div>

      {/* Lead Status Chart */}
      {loadingChart ? (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-6 flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-blue-500"></div>
        </div>
      ) : (
        <LeadStatusChart data={leadStatusData} />
      )}

      {/* Filters and Search */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="all">All Leads</option>
            <option value="Interested">Interested</option>
            <option value="Call Answered">Call Answered</option>
            <option value="Call Not Answered">Call Not Answered</option>
            <option value="Not Interested">Not Interested</option>
            <option value="Online Demo Taken">Online Demo Taken</option>
            <option value="Gurgaon Campus Visited">Gurgaon Campus Visited</option>
            <option value="Online Admission Taken">Online Admission Taken</option>
            <option value="Gurgaon Admission Taken">Gurgaon Admission Taken</option>
          </select>
          <div className="relative">
            <input 
              type="text"
              placeholder="Search Student"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border border-gray-300 px-3 py-2 rounded-lg pl-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <Search className="h-5 w-5 absolute left-2 top-3 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No.
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Class
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Label
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Followups
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center">
                    Created At
                    {sortConfig.key === 'created_at' && (
                      <span className="ml-2">
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLeads.map((lead, index) => {
                const student = studentDetails[lead.s_id] || {};
                const leadFollowups = followups[lead.id] || [];
                const hasIncompleteFoll = Array.isArray(leadFollowups) 
                  ? leadFollowups.some(f => f.status === 'incomplete')
                  : false;
                return (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          {student.f_name ? student.f_name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{student.f_name || 'Unknown Student'}</div>
                          <div className="text-sm text-gray-500">{student.phonenumber || 'N/A'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                      {student.c_entry || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getLeadStatusColor(lead.lead_status)}`}
                      >
                        {lead.lead_status}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lead.lead_label}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      {leadFollowups.length > 0 ? (
                        <div className="flex justify-center items-center">
                          <button
                            onClick={() => handleViewFollowups(lead.id)}
                            className="text-sm text-blue-600 hover:underline flex items-center space-x-1"
                          >
                            <span>{leadFollowups.length} Followup(s)</span>
                            {hasIncompleteFoll && (
                              <AlertCircle className="text-yellow-500 w-4 h-4" />
                            )}
                          </button>
                        </div>
                      ) : (
                        <div className="text-center">
                          <span className="text-sm text-gray-400">No Followups</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(lead.created_at)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => handleViewStudent(lead.s_id)}
                          className="text-blue-500 hover:text-blue-700 p-2 rounded-full hover:bg-blue-50"
                          title="View Student"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleWhatsAppContact(student.phonenumber)}
                          className="text-green-500 hover:text-green-700 p-2 rounded-full hover:bg-green-50"
                          title="Contact on WhatsApp"
                        >
                          <MessageCircle size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* No Data */}
          {filteredLeads.length === 0 && !loading && (
            <div className="text-center py-10 text-gray-500">
              {leads.length > 0 ? 'No leads match your current filters.' : 'No student leads found.'}
            </div>
          )}
        </div>
      )}
      
      {/* Followup Modal */}
      {selectedLeadFollowups && (
        <FollowupModal 
          followups={selectedLeadFollowups} 
          onClose={() => setSelectedLeadFollowups(null)} 
        />
      )}

      {/* Pagination */}
      {filteredLeads.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div>
            <p className="text-sm text-gray-700">
              Showing 1 to {filteredLeads.length} of {filteredLeads.length} results
            </p>
          </div>
          <div className="flex space-x-1">
            <button className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50">
              &lt;
            </button>
            <button className="px-3 py-1 rounded border border-blue-500 bg-blue-50 text-blue-600">
              1
            </button>
            <button className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50">
              &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentLead;